import {
  AddOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  RemoveCircleOutlined,
  WorkOutlineOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type {
  ExternalCareer,
  ExternalCareerEmploymentType,
  ProfileCareerChangeInput,
  ProfileCareerChangeItem,
} from './model';
import { DefinitionList, DialogSurface, EmptyState, FlowSteps, FormErrorSummary, FormTextField, SectionCard, StatusBadge, ValueBadge, designTokens, type FormErrorSummaryItem } from '../../../design-system';
import { profileDateValue, profileTextValue } from './profileDisplay';

export type ExternalCareerChangeAction = ProfileCareerChangeItem['action'];

export type ExternalCareerChangeSeed = {
  action: ExternalCareerChangeAction;
  career?: ExternalCareer;
};

type ExternalCareerChangeDialogProps = {
  open: boolean;
  careers: ExternalCareer[];
  seed?: ExternalCareerChangeSeed;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (input: ProfileCareerChangeInput) => void;
};

type FormField = 'employerName' | 'jobTitle' | 'startDate' | 'endDate';
type FormErrors = Partial<Record<FormField | 'row', string>>;

type DraftChange = {
  clientId: string;
  item: ProfileCareerChangeItem;
  source?: ExternalCareer;
};

const maxChanges = 20;
const fieldOrder: FormField[] = ['employerName', 'jobTitle', 'startDate', 'endDate'];

const employmentTypeOptions: Array<{ value: ExternalCareerEmploymentType; label: string }> = [
  { value: 'FULL_TIME', label: '정규직' },
  { value: 'PART_TIME', label: '파트타임' },
  { value: 'CONTRACT', label: '계약직' },
  { value: 'INTERN', label: '인턴' },
  { value: 'FREELANCE', label: '프리랜서' },
  { value: 'SELF_EMPLOYED', label: '자영업' },
  { value: 'OTHER', label: '기타' },
];

function today() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

function actionLabel(action: ExternalCareerChangeAction) {
  if (action === 'CREATE') return '추가';
  if (action === 'UPDATE') return '수정';
  return '종료';
}

function actionTone(action: ExternalCareerChangeAction) {
  if (action === 'CREATE') return 'success' as const;
  if (action === 'UPDATE') return 'info' as const;
  return 'danger' as const;
}

function employmentTypeLabel(value?: string) {
  return employmentTypeOptions.find((option) => option.value === value)?.label ?? value ?? '고용 형태 미등록';
}

function dayAfter(value?: string) {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

function createDraft(action: ExternalCareerChangeAction, career?: ExternalCareer): DraftChange {
  const reference = career ? { id: career.id } : undefined;
  return {
    clientId: crypto.randomUUID(),
    source: career,
    item: action === 'END'
      ? { action, career: reference }
      : {
        action,
        career: action === 'UPDATE' ? reference : undefined,
        employerName: career?.employerName ?? '',
        jobTitle: career?.jobTitle ?? '',
        department: career?.department,
        employmentType: career?.employmentType ?? 'FULL_TIME',
        startDate: career?.startDate ?? '',
        endDate: career?.endDate,
        description: career?.description,
      },
  };
}

function validateDraft(draft: DraftChange): FormErrors {
  if (draft.item.action === 'END') return {};
  const errors: FormErrors = {};
  if (!draft.item.employerName?.trim()) errors.employerName = '회사명을 입력해 주세요.';
  if (!draft.item.jobTitle?.trim()) errors.jobTitle = '직무 또는 직책을 입력해 주세요.';
  if (!draft.item.startDate) errors.startDate = '시작일을 입력해 주세요.';
  if (draft.item.endDate && draft.item.startDate && draft.item.endDate <= draft.item.startDate) {
    errors.endDate = '종료일은 시작일보다 이후여야 합니다.';
  }
  if (draft.item.action === 'UPDATE' && draft.source) {
    const item = normalizeItem(draft.item);
    const same = item.employerName === draft.source.employerName.trim()
      && item.jobTitle === draft.source.jobTitle.trim()
      && (item.department ?? '') === (draft.source.department?.trim() ?? '')
      && item.employmentType === draft.source.employmentType
      && item.startDate === draft.source.startDate
      && (item.endDate ?? '') === (draft.source.endDate ?? '')
      && (item.description ?? '') === (draft.source.description?.trim() ?? '');
    if (same) errors.row = '수정된 내용이 없습니다. 한 항목 이상 변경해 주세요.';
  }
  return errors;
}

function normalizeItem(item: ProfileCareerChangeItem): ProfileCareerChangeItem {
  if (item.action === 'END') return { action: item.action, career: item.career };
  return {
    ...item,
    employerName: item.employerName?.trim(),
    jobTitle: item.jobTitle?.trim(),
    department: item.department?.trim() || undefined,
    endDate: item.endDate || undefined,
    description: item.description?.trim() || undefined,
  };
}

function draftTitle(draft: DraftChange) {
  return draft.item.employerName?.trim() || draft.source?.employerName || '새 외부 경력';
}

function draftSubtitle(draft: DraftChange) {
  if (draft.item.action === 'END') return draft.source?.jobTitle || '종료할 경력';
  return draft.item.jobTitle?.trim() || '직무/직책을 입력해 주세요.';
}

function period(startDate?: string, endDate?: string) {
  return `${profileDateValue(startDate) || '미입력'} ~ ${profileDateValue(endDate) || '현재'}`;
}

function changedValue(before: string | undefined, after: string | undefined) {
  const current = profileTextValue(before) || '미입력';
  const next = profileTextValue(after) || '미입력';
  return current === next ? next : `${current} → ${next}`;
}

export function ExternalCareerChangeDialog({
  open,
  careers,
  seed,
  busy,
  error,
  onClose,
  onSubmit,
}: ExternalCareerChangeDialogProps) {
  const [step, setStep] = useState<0 | 1>(0);
  const [effectiveDate, setEffectiveDate] = useState(today);
  const [drafts, setDrafts] = useState<DraftChange[]>([]);
  const [editingId, setEditingId] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, FormErrors>>({});
  const [effectiveDateError, setEffectiveDateError] = useState('');
  const [errorFocusRequest, setErrorFocusRequest] = useState(0);
  const [localError, setLocalError] = useState('');
  const [focusTarget, setFocusTarget] = useState<{ id: string; field: FormField }>();
  const errorRef = useRef<HTMLDivElement>(null);
  const effectiveDateRef = useRef<HTMLInputElement>(null);
  const inputRefs = useRef(new Map<string, HTMLInputElement>());
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const initial = seed ? createDraft(seed.action, seed.career) : undefined;
    setStep(0);
    setEffectiveDate(today());
    setDrafts(initial ? [initial] : []);
    setEditingId(initial?.clientId);
    setFieldErrors({});
    setEffectiveDateError('');
    setErrorFocusRequest(0);
    setLocalError('');
    setFocusTarget(undefined);
  }, [open, seed]);

  useEffect(() => {
    if (!focusTarget || editingId !== focusTarget.id) return;
    const frame = window.requestAnimationFrame(() => {
      inputRefs.current.get(`${focusTarget.id}:${focusTarget.field}`)?.focus();
      setFocusTarget(undefined);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editingId, focusTarget]);

  useEffect(() => {
    if (!error) return;
    window.requestAnimationFrame(() => errorRef.current?.focus());
  }, [error]);

  const targetActionById = useMemo(() => new Map(
    drafts.flatMap((draft) => draft.item.career?.id ? [[draft.item.career.id, draft.item.action] as const] : []),
  ), [drafts]);

  const counts = useMemo(() => ({
    CREATE: drafts.filter((draft) => draft.item.action === 'CREATE').length,
    UPDATE: drafts.filter((draft) => draft.item.action === 'UPDATE').length,
    END: drafts.filter((draft) => draft.item.action === 'END').length,
  }), [drafts]);

  const setInputRef = (id: string, field: FormField) => (node: HTMLInputElement | null) => {
    const key = `${id}:${field}`;
    if (node) inputRefs.current.set(key, node);
    else inputRefs.current.delete(key);
  };

  const clearFieldError = (id: string, field: FormField) => {
    setFieldErrors((current) => {
      if (!current[id]?.[field]) return current;
      const nextErrors = { ...current[id] };
      delete nextErrors[field];
      return { ...current, [id]: nextErrors };
    });
  };

  const updateDraft = (id: string, patch: Partial<ProfileCareerChangeItem>, clearField?: FormField) => {
    setDrafts((current) => current.map((draft) => draft.clientId === id
      ? { ...draft, item: { ...draft.item, ...patch } }
      : draft));
    if (clearField) clearFieldError(id, clearField);
    setFieldErrors((current) => {
      if (!current[id]?.row) return current;
      const nextErrors = { ...current[id] };
      delete nextErrors.row;
      return { ...current, [id]: nextErrors };
    });
    setLocalError('');
  };

  const addDraft = (action: ExternalCareerChangeAction, career?: ExternalCareer) => {
    if (busy) return;
    if (drafts.length >= maxChanges) {
      setLocalError(`한 번의 요청에는 최대 ${maxChanges}건까지 포함할 수 있습니다.`);
      return;
    }
    if (career && targetActionById.has(career.id)) {
      setLocalError(`${career.employerName} 경력은 이미 변경 목록에 포함되어 있습니다.`);
      return;
    }
    const next = createDraft(action, career);
    setDrafts((current) => [...current, next]);
    setEditingId(next.clientId);
    setLocalError('');
    if (action !== 'END') setFocusTarget({ id: next.clientId, field: 'employerName' });
  };

  const removeDraft = (id: string) => {
    if (busy) return;
    setDrafts((current) => current.filter((draft) => draft.clientId !== id));
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    setEditingId((current) => current === id ? undefined : current);
    setLocalError('');
  };

  const review = () => {
    if (!effectiveDate) {
      setEffectiveDateError('효력 기준일을 입력해 주세요.');
      setErrorFocusRequest((current) => current + 1);
      return;
    }
    if (!drafts.length) {
      setLocalError('변경 내용을 한 건 이상 추가해 주세요.');
      window.requestAnimationFrame(() => addButtonRef.current?.focus());
      return;
    }
    const normalizedDrafts = drafts.map((draft) => ({ ...draft, item: normalizeItem(draft.item) }));
    const nextErrors = Object.fromEntries(normalizedDrafts.map((draft) => [draft.clientId, validateDraft(draft)]));
    setFieldErrors(nextErrors);
    const invalid = normalizedDrafts.find((draft) => Object.keys(nextErrors[draft.clientId]).length > 0);
    if (invalid) {
      setLocalError('');
      setEditingId(invalid.clientId);
      setFocusTarget(undefined);
      setErrorFocusRequest((current) => current + 1);
      return;
    }
    setLocalError('');
    setDrafts(normalizedDrafts);
    setEditingId(undefined);
    setStep(1);
  };

  const summaryItems: FormErrorSummaryItem[] = [];
  if (step === 0) {
    if (effectiveDateError) summaryItems.push({
      id: 'effective-date', label: '효력 기준일', message: effectiveDateError,
      onActivate: () => effectiveDateRef.current?.focus(),
    });
    const fieldLabels: Record<FormField | 'row', string> = {
      employerName: '회사명', jobTitle: '직무/직책', startDate: '시작일', endDate: '종료일', row: '변경 내용',
    };
    drafts.forEach((draft, index) => {
      const errors = fieldErrors[draft.clientId] ?? {};
      [...fieldOrder, 'row' as const].forEach((field) => {
        if (!errors[field]) return;
        summaryItems.push({
          id: `${draft.clientId}-${field}`,
          label: `${index + 1}번째 경력 · ${fieldLabels[field]}`,
          message: errors[field],
          onActivate: () => {
            setEditingId(draft.clientId);
            setFocusTarget({ id: draft.clientId, field: field === 'row' ? 'employerName' : field });
          },
        });
      });
    });
  }

  const submit = () => {
    if (busy || !drafts.length) return;
    const items = drafts.map((draft) => normalizeItem(draft.item));
    onSubmit({ effectiveDate, careers: [items[0], ...items.slice(1)] });
  };

  const dialogTitle = seed?.action === 'CREATE' && drafts.length <= 1
    ? '외부 경력 추가 요청'
    : seed?.action === 'UPDATE' && drafts.length <= 1
      ? '외부 경력 수정 요청'
      : seed?.action === 'END' && drafts.length <= 1
        ? '외부 경력 종료 요청'
        : '외부 경력 변경 요청';

  const renderEditor = (draft: DraftChange) => {
    if (draft.item.action === 'END') {
      return <Box sx={{ p: 1.5 }}>
        <Stack spacing={1.25}>
          <Alert severity="warning">승인되면 현재 기록이 종료됩니다. 기존 경력 이력은 삭제되지 않습니다.</Alert>
          {draft.source && <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 720 }}>{draft.source.employerName}</Typography>
            <Typography variant="caption" color="text.secondary">{draft.source.jobTitle} · {period(draft.source.startDate, draft.source.endDate)}</Typography>
          </Paper>}
        </Stack>
      </Box>;
    }
    const errors = fieldErrors[draft.clientId] ?? {};
    return <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Stack spacing={2}>
        {errors.row && <Alert severity="warning">{errors.row}</Alert>}
        {draft.item.action === 'UPDATE' && draft.source && <Alert severity="info">
          {draft.source.employerName} · {draft.source.jobTitle} 경력의 변경 내용을 입력합니다.
        </Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
          <FormTextField
            required
            fullWidth
            inputRef={setInputRef(draft.clientId, 'employerName')}
            label="회사명"
            placeholder="예: 테크컴퍼니"
            value={draft.item.employerName ?? ''}
            error={Boolean(errors.employerName)}
            helperText={errors.employerName}
            onChange={(event) => updateDraft(draft.clientId, { employerName: event.target.value }, 'employerName')}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 200 } }}
          />
          <FormTextField
            required
            fullWidth
            inputRef={setInputRef(draft.clientId, 'jobTitle')}
            label="직무/직책"
            placeholder="예: Product Manager"
            value={draft.item.jobTitle ?? ''}
            error={Boolean(errors.jobTitle)}
            helperText={errors.jobTitle}
            onChange={(event) => updateDraft(draft.clientId, { jobTitle: event.target.value }, 'jobTitle')}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 200 } }}
          />
          <FormTextField
            fullWidth
            label="부서 (선택)"
            placeholder="예: Product"
            value={draft.item.department ?? ''}
            onChange={(event) => updateDraft(draft.clientId, { department: event.target.value || undefined })}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 200 } }}
          />
          <FormTextField
            select
            required
            fullWidth
            label="고용 형태"
            value={draft.item.employmentType ?? 'FULL_TIME'}
            onChange={(event) => updateDraft(draft.clientId, { employmentType: event.target.value as ExternalCareerEmploymentType })}
            slotProps={{ inputLabel: { shrink: true } }}
          >
            {employmentTypeOptions.map((option) => <MenuItem key={option.value} value={option.value}><ValueBadge label={option.label} /></MenuItem>)}
          </FormTextField>
          <FormTextField
            required
            fullWidth
            type="date"
            inputRef={setInputRef(draft.clientId, 'startDate')}
            label="시작일"
            value={draft.item.startDate ?? ''}
            error={Boolean(errors.startDate)}
            helperText={errors.startDate}
            onChange={(event) => {
              updateDraft(draft.clientId, { startDate: event.target.value }, 'startDate');
              clearFieldError(draft.clientId, 'endDate');
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <FormTextField
            fullWidth
            type="date"
            inputRef={setInputRef(draft.clientId, 'endDate')}
            label="종료일 (선택)"
            value={draft.item.endDate ?? ''}
            error={Boolean(errors.endDate)}
            helperText={errors.endDate || '현재 근무 중이라면 비워 두세요.'}
            onChange={(event) => updateDraft(draft.clientId, { endDate: event.target.value || undefined }, 'endDate')}
            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: dayAfter(draft.item.startDate) } }}
          />
        </Box>
        <FormTextField
          fullWidth
          multiline
          minRows={3}
          maxRows={7}
          label="주요 업무 및 성과 (선택)"
          placeholder="담당한 업무, 성과, 사용한 기술 등을 간단히 적어 주세요."
          value={draft.item.description ?? ''}
          characterCount={{ current: (draft.item.description ?? '').length, max: 2000 }}
          onChange={(event) => updateDraft(draft.clientId, { description: event.target.value || undefined })}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 2000 } }}
        />
      </Stack>
    </Box>;
  };

  const renderReview = (draft: DraftChange) => {
    const item = draft.item;
    const source = draft.source;
    const target = source
      ? `${source.employerName} · ${source.jobTitle}`
      : item.career?.descriptor || (item.action === 'CREATE' ? '새 외부 경력' : '대상 경력 정보 없음');
    const items: Array<readonly [string, ReactNode]> = [
      ['변경 유형', <StatusBadge label={actionLabel(item.action)} tone={actionTone(item.action)} />],
      ['대상', target],
    ];
    if (item.action !== 'END') items.push(
      ['회사명', item.action === 'UPDATE' ? changedValue(source?.employerName, item.employerName) : profileTextValue(item.employerName) || '미입력'],
      ['직무/직책', item.action === 'UPDATE' ? changedValue(source?.jobTitle, item.jobTitle) : profileTextValue(item.jobTitle) || '미입력'],
      ['부서', item.action === 'UPDATE' ? changedValue(source?.department, item.department) : profileTextValue(item.department) || '미입력'],
      ['고용 형태', item.action === 'UPDATE' && source?.employmentType !== item.employmentType ? <>{source?.employmentType ? <ValueBadge label={employmentTypeLabel(source.employmentType)} /> : '고용 형태 미등록'} → {item.employmentType ? <ValueBadge label={employmentTypeLabel(item.employmentType)} /> : '고용 형태 미등록'}</> : item.employmentType ? <ValueBadge label={employmentTypeLabel(item.employmentType)} /> : '고용 형태 미등록'],
      ['근무 기간', item.action === 'UPDATE' ? changedValue(period(source?.startDate, source?.endDate), period(item.startDate, item.endDate)) : period(item.startDate, item.endDate)],
      ['주요 업무 및 성과', item.action === 'UPDATE' ? changedValue(source?.description, item.description) : profileTextValue(item.description) || '미입력'],
    );
    return <Paper key={draft.clientId} component="li" variant="outlined" sx={{ overflow: 'hidden', listStyle: 'none' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', p: 1.5 }}>
        <StatusBadge label={actionLabel(item.action)} tone={actionTone(item.action)} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 720 }}>{draftTitle(draft)}</Typography>
          <Typography variant="caption" color="text.secondary">{draftSubtitle(draft)}</Typography>
        </Box>
      </Stack>
      <Divider sx={{ mx: 1.5 }} />
      <Box sx={{ p: 1.5 }}>
        <DefinitionList items={items} />
        {item.action === 'END' && <Alert severity="warning" sx={{ mt: 1.25 }}>이 경력의 현재 기록을 종료하고 기존 이력은 보존합니다.</Alert>}
      </Box>
    </Paper>;
  };

  return <DialogSurface
    open={open}
    onClose={onClose}
    busy={busy}
    onSubmit={() => { if (step === 0) review(); else submit(); }}
    title={dialogTitle}
    help="여러 경력 변경을 하나의 승인 요청으로 제출합니다. 승인되면 모든 변경이 함께 즉시 반영됩니다."
    titleLeading={<Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: designTokens.color.actionSoft, color: designTokens.color.action }}><WorkOutlineOutlined /></Avatar>}
    titleAdornment={<ValueBadge label="한 번의 승인 요청" tone="accent" />}
    headerContent={<FlowSteps activeStep={step} ariaLabel="외부 경력 변경 단계" steps={['편집', '변경 검토']} />}
    closeLabel={`${dialogTitle} 닫기`}
    size="large"
    mobilePresentation="fullscreen"
    contentDividers
    contentMode="fill"
    actions={<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%', alignItems: { sm: 'center' } }}>
      {step === 1 && <Button type="button" onClick={() => setStep(0)} disabled={busy} sx={{ width: { xs: '100%', sm: 'auto' } }}>편집으로 돌아가기</Button>}
      <Box sx={{ flex: 1 }} />
      <Button type="button" onClick={onClose} disabled={busy} sx={{ width: { xs: '100%', sm: 'auto' } }}>취소</Button>
      <Button type="submit" variant="contained" disabled={busy || !drafts.length} sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 180 } }}>
        {busy ? '요청하는 중…' : step === 0 ? `${drafts.length}건 변경 검토` : `${drafts.length}건을 한 번의 승인 요청으로 제출`}
      </Button>
    </Stack>}
  >
        <Stack spacing={2}>
          <FormErrorSummary items={summaryItems} focusRequest={errorFocusRequest} />
          {(error || localError) && <Alert ref={errorRef} tabIndex={-1} severity="error">{error || localError}</Alert>}

          {step === 0 ? <>
            <SectionCard title="공통 반영 기준" help="이 요청에 포함된 모든 경력 변경에 같은 효력 기준일을 사용합니다.">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 1.5, alignItems: { sm: 'center' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 720 }}>승인 완료 즉시 함께 적용</Typography>
                </Box>
                <FormTextField
                  required
                  type="date"
                  inputRef={effectiveDateRef}
                  label="효력 기준일"
                  value={effectiveDate}
                  error={Boolean(effectiveDateError)}
                  helperText={effectiveDateError}
                  onChange={(event) => { setEffectiveDate(event.target.value); setEffectiveDateError(''); }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ width: { xs: '100%', sm: 240 } }}
                />
              </Stack>
            </SectionCard>

            <SectionCard
              title={`요청에 포함된 변경 ${drafts.length}건`}
              action={<Button ref={addButtonRef} size="small" startIcon={<AddOutlined />} onClick={() => addDraft('CREATE')} disabled={busy || drafts.length >= maxChanges}>새 경력 추가</Button>}
            >
              {drafts.length ? <Box component="ol" sx={{ m: 0, p: 1.5, listStyle: 'none', display: 'grid', gap: 1 }}>
                {drafts.map((draft, index) => {
                  const expanded = editingId === draft.clientId;
                  const invalid = Object.keys(fieldErrors[draft.clientId] ?? {}).length > 0;
                  return <Paper key={draft.clientId} component="li" variant="outlined" sx={{ overflow: 'hidden', borderColor: invalid ? 'error.main' : 'divider' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ p: 1.25, alignItems: { sm: 'center' } }}>
                      <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: 22, textAlign: 'center' }}>{index + 1}</Typography>
                        <StatusBadge label={actionLabel(draft.item.action)} tone={actionTone(draft.item.action)} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 720 }}>{draftTitle(draft)}</Typography>
                          <Typography variant="caption" color={invalid ? 'error.main' : 'text.secondary'}>{invalid ? '입력 내용을 확인해 주세요.' : draftSubtitle(draft)}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        {draft.item.action !== 'END' && <Button
                          size="small"
                          startIcon={<EditOutlined />}
                          aria-expanded={expanded}
                          aria-label={`${draftTitle(draft)} 변경 ${expanded ? '편집 접기' : '편집하기'}`}
                          onClick={() => setEditingId(expanded ? undefined : draft.clientId)}
                          disabled={busy}
                        >{expanded ? '접기' : '편집'}</Button>}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineOutlined />}
                          aria-label={`${draftTitle(draft)} 변경 요청에서 제거`}
                          onClick={() => removeDraft(draft.clientId)}
                          disabled={busy}
                        >제거</Button>
                      </Stack>
                    </Stack>
                    {expanded && <><Divider sx={{ mx: 1.25 }} />{renderEditor(draft)}</>}
                  </Paper>;
                })}
              </Box> : <EmptyState>아래에서 현재 경력을 선택하거나 새 경력을 추가해 주세요.</EmptyState>}
              <Box sx={{ px: 1.5, pb: 1.5 }}><Typography variant="caption" color="text.secondary">한 번의 요청에 최대 {maxChanges}건까지 포함할 수 있습니다.</Typography></Box>
            </SectionCard>

            <SectionCard title="현재 경력에서 변경 추가">
              {careers.length ? <Stack divider={<Divider />}>
                {careers.map((career) => {
                  const includedAction = targetActionById.get(career.id);
                  return <Stack key={career.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ px: 1.5, py: 1.25, alignItems: { sm: 'center' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 720 }}>{career.employerName}</Typography>
                        <ValueBadge label={employmentTypeLabel(career.employmentType)} />
                        {includedAction && <StatusBadge label={`${actionLabel(includedAction)}에 포함`} tone={actionTone(includedAction)} />}
                      </Stack>
                      <Typography variant="caption" component="div" color="text.secondary">{career.jobTitle} · {period(career.startDate, career.endDate)}</Typography>
                    </Box>
                    {!includedAction && <Stack direction="row" spacing={0.5}>
                      <Button size="small" startIcon={<EditOutlined />} aria-label={`${career.employerName} 경력 수정에 추가`} onClick={() => addDraft('UPDATE', career)} disabled={busy || drafts.length >= maxChanges}>수정에 추가</Button>
                      <Button size="small" color="error" startIcon={<RemoveCircleOutlined />} aria-label={`${career.employerName} 경력 종료에 추가`} onClick={() => addDraft('END', career)} disabled={busy || drafts.length >= maxChanges}>종료에 추가</Button>
                    </Stack>}
                  </Stack>;
                })}
              </Stack> : <EmptyState>현재 등록된 경력이 없습니다. 새 경력을 추가해 주세요.</EmptyState>}
            </SectionCard>
          </> : <>
            <Alert severity="info">
              아래 {drafts.length}건은 하나의 승인 요청으로 제출됩니다. 승인되면 모든 변경이 함께 즉시 반영되고, 같은 효력 기준일이 기록됩니다.
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">변경 {drafts.length}건 검토</Typography>
                <Typography variant="body2" color="text.secondary">효력 기준일 {effectiveDate}</Typography>
              </Box>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                <StatusBadge label={`추가 ${counts.CREATE}`} tone="success" />
                <StatusBadge label={`수정 ${counts.UPDATE}`} tone="info" />
                <StatusBadge label={`종료 ${counts.END}`} tone="danger" />
              </Stack>
            </Stack>
            <Box component="ol" aria-label="제출할 외부 경력 변경" sx={{ m: 0, p: 0, display: 'grid', gap: 1 }}>
              {drafts.map(renderReview)}
            </Box>
          </>}
        </Stack>
  </DialogSurface>;
}
